import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Toaster } from 'sonner';
import { ArrowLeft, Loader2, Frame, Instagram, Globe, MapPin } from 'lucide-react';
import CustomCursor from '@/components/CustomCursor';
import Header from '@/components/Header';
import ArtworkCard from '@/components/ArtworkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicArtistPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/artists/${slug}`)
      .then((r) => setPage(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'Artist page not found' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /><Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
        <CustomCursor /><Header />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <Frame className="w-8 h-8 text-[#333] mx-auto" />
            <p className="text-[13px] font-mono text-[#888] mt-4">{error}</p>
            <button
              onClick={() => navigate('/artists')}
              className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-[#90E0EF] hover:text-white transition-colors"
            >
              ← BACK TO ARTISTS
            </button>
          </div>
        </div>
      </div>
    );
  }

  const websiteWorks = page.website_works || [];

  return (
    <div className="min-h-screen bg-[#050505] text-[#f6f6f6]">
      <CustomCursor />
      <Header />
      <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: '#111', border: '1px solid #333', borderRadius: 0, fontFamily: '"JetBrains Mono", monospace', fontSize: '12px' } }} />

      <main className="relative z-10" style={{ paddingTop: 'calc(var(--header-height) + 24px)' }}>
        <div className="px-6 md:px-10 lg:px-16 py-4 border-b border-[#111]">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/artists')} className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> All artists
            </button>
            <a href="/artworks" className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] hover:text-white transition-colors">
              All artworks →
            </a>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16">
          {/* Artist hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 md:gap-12 items-start pb-10 border-b border-[#111]"
          >
            <div className="aspect-square w-full max-w-[220px] border border-[#111] bg-[#0a0a0a] overflow-hidden">
              {page.pfp_url ? (
                <img src={page.pfp_url} alt={page.artist_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Frame className="w-8 h-8 text-[#222]" />
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#90E0EF]">Artist</div>
              <h1 className="mt-2 text-4xl md:text-5xl font-outfit font-semibold text-white leading-[1] tracking-tight uppercase">
                {page.artist_name}
              </h1>
              {page.location && (
                <div className="mt-3 flex items-center gap-1.5 text-[12px] font-mono text-[#c8c8c8]">
                  <MapPin className="w-3.5 h-3.5 text-[#666]" />
                  {page.location}
                </div>
              )}

              {(page.website || page.instagram) && (
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  {page.website && (
                    <a
                      href={page.website.startsWith('http') ? page.website : `https://${page.website}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-[#90E0EF] hover:text-white transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5" /> {page.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {page.instagram && (
                    <a
                      href={page.instagram.startsWith('http') ? page.instagram : `https://instagram.com/${page.instagram.replace(/^@/, '')}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-[#90E0EF] hover:text-white transition-colors"
                    >
                      <Instagram className="w-3.5 h-3.5" /> {page.instagram.replace(/^@/, '')}
                    </a>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Bio */}
          {page.bio && (
            <section className="pt-10 max-w-3xl">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] mb-3">Bio</div>
              <p className="text-[14px] leading-relaxed text-[#c8c8c8] whitespace-pre-line">
                {page.bio}
              </p>
            </section>
          )}

          {/* Website-selected artist_works (between Bio and Statement) */}
          {websiteWorks.length > 0 && (
            <section className="mt-12" data-testid="artist-website-works">
              <div className="mb-6 flex items-baseline justify-between">
                <h2 className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Selected work</h2>
                <span className="text-[10px] font-mono text-[#333]">
                  {websiteWorks.length} {websiteWorks.length === 1 ? 'piece' : 'pieces'}
                </span>
              </div>
              {websiteWorks.length === 1 ? (
                <motion.figure
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  className="relative"
                >
                  <div className="aspect-[4/5] max-w-3xl mx-auto border border-[#111] overflow-hidden bg-[#0a0a0a]">
                    <img
                      src={websiteWorks[0].image_url}
                      alt={websiteWorks[0].title || page.artist_name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {websiteWorks[0].title && (
                    <figcaption className="mt-3 text-center text-[11px] font-mono text-[#888] uppercase tracking-[0.14em]">
                      {websiteWorks[0].title}
                    </figcaption>
                  )}
                </motion.figure>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {websiteWorks.map((w) => (
                    <motion.figure
                      key={w.id}
                      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-40px' }}
                    >
                      <div className="aspect-[4/5] border border-[#111] overflow-hidden bg-[#0a0a0a] group">
                        <img
                          src={w.image_url}
                          alt={w.title || page.artist_name}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                        />
                      </div>
                      {w.title && (
                        <figcaption className="mt-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[#888]">
                          {w.title}
                        </figcaption>
                      )}
                    </motion.figure>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Statement */}
          {page.statement && (
            <section className="mt-12 max-w-3xl">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] mb-3">Statement</div>
              <p className="text-[13px] leading-relaxed text-[#a8a8a8] italic whitespace-pre-line">
                {page.statement}
              </p>
            </section>
          )}

          {/* Published artworks */}
          <section className="mt-16" data-testid="artist-artworks-section">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="text-2xl font-outfit font-semibold text-white tracking-tight uppercase">
                Available Artworks
              </h2>
              <span className="text-[11px] font-mono text-[#666]">
                {page.artworks.length} {page.artworks.length === 1 ? 'work' : 'works'}
              </span>
            </div>

            {page.artworks.length === 0 ? (
              <div className="text-center py-16 border border-[#111]">
                <Frame className="w-8 h-8 text-[#222] mx-auto" />
                <p className="text-[13px] font-mono text-[#666] mt-4">No published artworks yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {page.artworks.map((a) => (
                  <ArtworkCard key={a.id} artwork={a} testId={`artist-artwork-${a.slug}`} />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
